import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
    DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, MapPin } from "lucide-react";

const TYPES = [
    { value: "allocated_bay", label: "Allocated bay" },
    { value: "visitor_bay", label: "Visitor bay" },
    { value: "communal_road", label: "Communal road" },
    { value: "communal_walk", label: "Communal walkway" },
    { value: "communal_carpark", label: "Communal car park" },
    { value: "other", label: "Other" },
];

const TYPE_COLOR = {
    allocated_bay: "#2563EB",
    visitor_bay: "#D97706",
    communal_road: "#7C3AED",
    communal_walk: "#059669",
    communal_carpark: "#475569",
    other: "#94A3B8",
};

function emptyForm() {
    return {
        code: "",
        label: "",
        type: "allocated_bay",
        description: "",
        assigned_property_id: "",
        coords_x: "",
        coords_y: "",
    };
}

export default function EstateMapAdmin() {
    const [settings, setSettings] = useState(null);
    const [locations, setLocations] = useState([]);
    const [properties, setProperties] = useState([]);
    const [kbDocs, setKbDocs] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [showArchived, setShowArchived] = useState(false);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // location id or 'new'
    const [form, setForm] = useState(emptyForm());
    const [open, setOpen] = useState(false);
    const [pickPin, setPickPin] = useState(false);
    const [mapBlobUrl, setMapBlobUrl] = useState(null);
    const fileRef = useRef(null);
    const [settingsForm, setSettingsForm] = useState({
        issuing_agency_name: "",
        issuing_agency_contact: "",
    });
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, l, p, d, sg] = await Promise.all([
                api.get("/estate/settings"),
                api.get("/locations", { params: { include_deleted: showArchived } }),
                api.get("/properties"),
                api.get("/documents", { params: { scope: "estate_kb" } }),
                api.get("/location-suggestions"),
            ]);
            setSettings(s.data);
            setLocations(l.data || []);
            setProperties(p.data || []);
            setKbDocs(d.data || []);
            setSuggestions(sg.data || []);
            setSettingsForm({
                issuing_agency_name: s.data.issuing_agency_name || "",
                issuing_agency_contact: s.data.issuing_agency_contact || "",
            });
        } catch {
            toast.error("Failed to load estate map data");
        } finally {
            setLoading(false);
        }
    }, [showArchived]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        let revoke;
        const loadMap = async () => {
            if (!settings?.map_document_id) {
                setMapBlobUrl(null);
                return;
            }
            try {
                const res = await api.get(`/documents/${settings.map_document_id}/download`, { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                revoke = url;
                setMapBlobUrl(url);
            } catch {
                setMapBlobUrl(null);
            }
        };
        loadMap();
        return () => { if (revoke) URL.revokeObjectURL(revoke); };
    }, [settings?.map_document_id]);

    const saveSettings = async (patch) => {
        try {
            const res = await api.put("/estate/settings", patch);
            setSettings(res.data);
            toast.success("Settings updated");
        } catch (e) {
            toast.error("Failed to update settings");
        }
    };

    const uploadMap = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", "Estate Layout Map");
        fd.append("description", "Estate layout map used by residents to pinpoint locations.");
        fd.append("scope", "estate_kb");
        fd.append("visibility", "estate_shared");
        try {
            const res = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await saveSettings({ map_document_id: res.data.id });
            await load();
            toast.success("Map uploaded and set");
            if (fileRef.current) fileRef.current.value = "";
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Map upload failed");
        }
    };

    const openCreate = () => {
        setEditing("new");
        setForm(emptyForm());
        setOpen(true);
    };

    const openEdit = (loc) => {
        setEditing(loc.id);
        setForm({
            code: loc.code,
            label: loc.label,
            type: loc.type,
            description: loc.description || "",
            assigned_property_id: loc.assigned_property_id || "",
            coords_x: loc.coords_x != null ? String(loc.coords_x) : "",
            coords_y: loc.coords_y != null ? String(loc.coords_y) : "",
        });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        const body = {
            code: form.code.trim(),
            label: form.label.trim(),
            type: form.type,
            description: form.description || null,
            assigned_property_id: form.assigned_property_id || null,
            coords_x: form.coords_x === "" ? null : parseFloat(form.coords_x),
            coords_y: form.coords_y === "" ? null : parseFloat(form.coords_y),
        };
        try {
            if (editing === "new") {
                await api.post("/locations", body);
                toast.success("Location added");
            } else {
                await api.patch(`/locations/${editing}`, body);
                toast.success("Location updated");
            }
            setOpen(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Archive this location? It will be hidden from residents. Existing PCN appeals that reference it will keep their record intact.")) return;
        try {
            const res = await api.delete(`/locations/${id}`);
            if (res.data?.referenced_by) {
                toast.success(`Archived. Still referenced by ${res.data.referenced_by} appeal draft(s).`);
            } else {
                toast.success("Location archived");
            }
            load();
        } catch {
            toast.error("Archive failed");
        }
    };

    const restore = async (id) => {
        try {
            await api.post(`/locations/${id}/restore`);
            toast.success("Location restored");
            load();
        } catch {
            toast.error("Restore failed");
        }
    };

    const reviewSuggestion = async (id, status, extras = {}) => {
        try {
            await api.post(`/location-suggestions/${id}/review`, { status, ...extras });
            toast.success(`Suggestion ${status}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Review failed");
        }
    };

    const onMapClick = (e) => {
        if (!pickPin || editing === null) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setForm((f) => ({ ...f, coords_x: x.toFixed(1), coords_y: y.toFixed(1) }));
        setPickPin(false);
        toast.success(`Pin placed at (${x.toFixed(1)}, ${y.toFixed(1)})`);
    };

    const filtered = useMemo(() => {
        if (filter === "all") return locations;
        return locations.filter((l) => l.type === filter);
    }, [locations, filter]);

    return (
        <div className="space-y-6" data-testid="estate-map-page">
            <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Admin</div>
                <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Estate map & locations</h1>
                <p className="text-sm text-slate-500 mt-1">Configure the single issuing agency, upload the estate layout, and curate the location list residents use when filing a PCN appeal.</p>
            </div>

            {/* Estate settings */}
            <section className="border border-slate-200 rounded-lg bg-white p-5 space-y-4" data-testid="estate-settings-section">
                <div className="text-xs uppercase tracking-widest text-slate-500">Estate parking contract</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Label>Issuing agency name</Label>
                        <Input
                            value={settingsForm.issuing_agency_name}
                            onChange={(e) => setSettingsForm({ ...settingsForm, issuing_agency_name: e.target.value })}
                            data-testid="settings-agency-name-input"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Appeals contact</Label>
                        <Input
                            value={settingsForm.issuing_agency_contact}
                            onChange={(e) => setSettingsForm({ ...settingsForm, issuing_agency_contact: e.target.value })}
                            placeholder="email or postal address"
                            data-testid="settings-agency-contact-input"
                        />
                    </div>
                </div>
                <Button
                    onClick={() => saveSettings({
                        issuing_agency_name: settingsForm.issuing_agency_name,
                        issuing_agency_contact: settingsForm.issuing_agency_contact,
                    })}
                    className="bg-slate-700 hover:bg-slate-800 text-white"
                    data-testid="save-settings-button"
                >
                    Save settings
                </Button>
            </section>

            {/* Map upload + preview */}
            <section className="border border-slate-200 rounded-lg bg-white p-5 space-y-4" data-testid="map-section">
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500">Estate layout map</div>
                        <div className="text-sm text-slate-500 mt-1">
                            Upload an image (PNG/JPG) to power the residents&apos; map-based location picker. Pins are positioned by percentage so the map can be any size.
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Input type="file" ref={fileRef} accept="image/*" className="max-w-xs" data-testid="map-file-input" />
                        <Button onClick={uploadMap} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="upload-map-button">
                            <Upload size={14} className="mr-2" /> Upload map
                        </Button>
                    </div>
                </div>

                {mapBlobUrl ? (
                    <div className="relative border border-slate-200 rounded-lg overflow-hidden bg-slate-50" data-testid="admin-map-container" onClick={onMapClick} style={{ cursor: pickPin ? "crosshair" : "default" }}>
                        <img src={mapBlobUrl} alt="Estate map" className="w-full h-auto block select-none" draggable={false} />
                        <div className="absolute inset-0 pointer-events-none">
                            {filtered.filter((l) => l.coords_x != null && l.coords_y != null).map((l) => (
                                <div
                                    key={l.id}
                                    className="absolute -translate-x-1/2 -translate-y-full"
                                    style={{ left: `${l.coords_x}%`, top: `${l.coords_y}%`, color: TYPE_COLOR[l.type] || "#475569" }}
                                    data-testid={`admin-map-pin-${l.code}`}
                                >
                                    <MapPin size={16} fill="white" strokeWidth={2.25} />
                                </div>
                            ))}
                            {pickPin && form.coords_x !== "" && form.coords_y !== "" && (
                                <div className="absolute -translate-x-1/2 -translate-y-full text-rose-600" style={{ left: `${form.coords_x}%`, top: `${form.coords_y}%` }}>
                                    <MapPin size={20} fill="currentColor" />
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="border border-dashed border-slate-200 rounded-lg px-4 py-8 text-center text-sm text-slate-500" data-testid="map-empty">
                        No map uploaded yet.
                    </div>
                )}
            </section>

            {/* Pending suggestions */}
            <section className="border border-slate-200 rounded-lg bg-white" data-testid="suggestions-section">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                    <div className="text-xs uppercase tracking-widest text-slate-500">
                        Resident suggestions ({suggestions.filter((s) => s.status === "pending").length} pending / {suggestions.length} total)
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="suggestions-table">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50">
                                <th className="table-header-cell">Submitted by</th>
                                <th className="table-header-cell">Label</th>
                                <th className="table-header-cell">Type</th>
                                <th className="table-header-cell">Coords</th>
                                <th className="table-header-cell">Status</th>
                                <th className="table-header-cell">When</th>
                                <th className="table-header-cell"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {suggestions.length === 0 && (
                                <tr><td colSpan={7} className="table-cell-tight text-slate-500" data-testid="suggestions-empty">No resident suggestions yet.</td></tr>
                            )}
                            {suggestions.map((s) => (
                                <tr key={s.id} className="border-b border-slate-100" data-testid={`suggestion-row-${s.id}`}>
                                    <td className="table-cell-tight text-slate-700">{s.owner_email}</td>
                                    <td className="table-cell-tight font-medium text-slate-900">
                                        {s.label}
                                        {s.description && <div className="text-xs text-slate-500 mt-0.5">{s.description}</div>}
                                    </td>
                                    <td className="table-cell-tight text-slate-700">{s.type.replace("_", " ")}</td>
                                    <td className="table-cell-tight text-slate-500 text-xs">
                                        {s.coords_x != null && s.coords_y != null
                                            ? `${Number(s.coords_x).toFixed(1)}, ${Number(s.coords_y).toFixed(1)}`
                                            : "Needs pin"}
                                    </td>
                                    <td className="table-cell-tight">
                                        <span
                                            data-testid={`suggestion-status-${s.id}`}
                                            className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded ${
                                                s.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" :
                                                s.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                                "bg-rose-50 text-rose-700 border-rose-200"
                                            }`}
                                        >
                                            {s.status}
                                        </span>
                                    </td>
                                    <td className="table-cell-tight text-slate-500 text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                                    <td className="table-cell-tight">
                                        {s.status === "pending" ? (
                                            <div className="flex items-center gap-3 text-xs">
                                                <button
                                                    type="button"
                                                    onClick={() => reviewSuggestion(s.id, "approved")}
                                                    className="text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
                                                    data-testid={`approve-suggestion-${s.id}`}
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const note = window.prompt("Reason for rejection (optional):") || null;
                                                        reviewSuggestion(s.id, "rejected", { reviewer_notes: note });
                                                    }}
                                                    className="text-rose-600 hover:text-rose-700 inline-flex items-center gap-1"
                                                    data-testid={`reject-suggestion-${s.id}`}
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        ) : s.approved_location_id ? (
                                            <span className="text-xs text-slate-500">
                                                → location created
                                            </span>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Locations table */}
            <section className="border border-slate-200 rounded-lg bg-white" data-testid="locations-section">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-wrap gap-3">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Locations ({locations.length})</div>
                    <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-600 flex items-center gap-1.5" data-testid="show-archived-toggle-wrapper">
                            <input
                                type="checkbox"
                                checked={showArchived}
                                onChange={(e) => setShowArchived(e.target.checked)}
                                data-testid="show-archived-toggle"
                            />
                            Show archived
                        </label>
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger className="w-48" data-testid="locations-filter-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All types</SelectItem>
                                {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Button onClick={openCreate} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="add-location-button">
                            <Plus size={14} className="mr-1.5" /> Add location
                        </Button>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="locations-table">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50">
                                <th className="table-header-cell">Code</th>
                                <th className="table-header-cell">Label</th>
                                <th className="table-header-cell">Type</th>
                                <th className="table-header-cell">Coords</th>
                                <th className="table-header-cell">Property</th>
                                <th className="table-header-cell">Status</th>
                                <th className="table-header-cell"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={7} className="table-cell-tight text-slate-500">Loading…</td></tr>}
                            {!loading && filtered.length === 0 && <tr><td colSpan={7} className="table-cell-tight text-slate-500">No locations.</td></tr>}
                            {filtered.map((l) => {
                                const prop = properties.find((p) => p.id === l.assigned_property_id);
                                return (
                                    <tr key={l.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${l.is_deleted ? "opacity-60" : ""}`} data-testid={`location-row-${l.code}`}>
                                        <td className="table-cell-tight font-mono text-xs text-slate-700">{l.code}</td>
                                        <td className="table-cell-tight font-medium text-slate-900">{l.label}</td>
                                        <td className="table-cell-tight text-slate-700">{l.type.replace("_", " ")}</td>
                                        <td className="table-cell-tight text-slate-500 text-xs">{l.coords_x != null ? `${l.coords_x}, ${l.coords_y}` : "—"}</td>
                                        <td className="table-cell-tight text-slate-600 text-xs">{prop ? prop.unit_number : "—"}</td>
                                        <td className="table-cell-tight">
                                            {l.is_deleted ? (
                                                <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded bg-slate-100 text-slate-600 border-slate-200" data-testid={`location-archived-${l.code}`}>archived</span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded bg-emerald-50 text-emerald-700 border-emerald-200">active</span>
                                            )}
                                        </td>
                                        <td className="table-cell-tight">
                                            <div className="flex items-center gap-3 text-xs">
                                                <button type="button" onClick={() => openEdit(l)} className="text-slate-700 hover:text-slate-900 inline-flex items-center gap-1" data-testid={`edit-location-${l.code}`}>
                                                    <Pencil size={12} /> Edit
                                                </button>
                                                {l.is_deleted ? (
                                                    <button type="button" onClick={() => restore(l.id)} className="text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1" data-testid={`restore-location-${l.code}`}>
                                                        Restore
                                                    </button>
                                                ) : (
                                                    <button type="button" onClick={() => remove(l.id)} className="text-rose-600 hover:text-rose-700 inline-flex items-center gap-1" data-testid={`delete-location-${l.code}`}>
                                                        <Trash2 size={12} /> Archive
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Add/edit dialog */}
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPickPin(false); }}>
                <DialogContent data-testid="location-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing === "new" ? "Add location" : "Edit location"}</DialogTitle>
                        <DialogDescription>
                            Configure a parking bay or communal area. Coordinates are percentages of the estate map and can be picked visually by clicking the map.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Code</Label>
                                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required data-testid="location-code-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Type</Label>
                                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                    <SelectTrigger data-testid="location-type-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Label</Label>
                            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required data-testid="location-label-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Description (optional)</Label>
                            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="location-description-input" />
                        </div>
                        {form.type === "allocated_bay" && (
                            <div className="space-y-1.5">
                                <Label>Assigned property</Label>
                                <Select value={form.assigned_property_id} onValueChange={(v) => setForm({ ...form, assigned_property_id: v })}>
                                    <SelectTrigger data-testid="location-property-select"><SelectValue placeholder="Pick a property" /></SelectTrigger>
                                    <SelectContent className="max-h-60">
                                        {properties.map((p) => (
                                            <SelectItem key={p.id} value={p.id}>{p.unit_number} — {p.address}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Map X (%)</Label>
                                <Input type="number" step="0.1" min="0" max="100" value={form.coords_x} onChange={(e) => setForm({ ...form, coords_x: e.target.value })} data-testid="location-x-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Map Y (%)</Label>
                                <Input type="number" step="0.1" min="0" max="100" value={form.coords_y} onChange={(e) => setForm({ ...form, coords_y: e.target.value })} data-testid="location-y-input" />
                            </div>
                        </div>
                        {mapBlobUrl && (
                            <div className="text-xs">
                                <button
                                    type="button"
                                    onClick={() => { setPickPin(true); setOpen(false); toast.info("Click on the map to place a pin."); }}
                                    className="text-slate-700 underline"
                                    data-testid="pick-pin-button"
                                >
                                    Pick coordinates by clicking the map →
                                </button>
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="save-location-button">
                                {editing === "new" ? "Add" : "Save"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
